/**
 * Aspect — Binary-mask → vector-outline extraction (pure math, no engine deps)
 * --------------------------------------------------------------
 * Turns a per-item pixel mask (from the Revive ID pass) into simplified
 * polygon contours that hug the item's visible silhouette:
 *
 *   1. Moore-neighborhood boundary tracing finds each connected component's
 *      outer contour in pixel space.
 *   2. Ramer–Douglas–Peucker simplification strips the pixel staircase down
 *      to a lightweight polygon (typically 20–80 points per item).
 *
 * Coordinates come back normalized 0..1 so the DOM/SVG layer stays correct
 * at any element size.
 */

/** Clockwise Moore neighborhood, starting east: E SE S SW W NW N NE. */
const MOORE = [
  [1, 0],
  [1, 1],
  [0, 1],
  [-1, 1],
  [-1, 0],
  [-1, -1],
  [0, -1],
  [1, -1],
];

/**
 * Trace the outer boundary of the connected component containing (sx, sy).
 * Standard Moore tracing with Jacob's stopping criterion (stop when the start
 * pixel is re-entered from the same backtrack direction).
 * @returns {number[][]} pixel-space contour [[x,y], ...]
 */
function traceBoundary(mask, w, h, sx, sy) {
  const inside = (x, y) => x >= 0 && y >= 0 && x < w && y < h && mask[y * w + x] === 1;
  const contour = [[sx, sy]];
  let cx = sx;
  let cy = sy;
  // The start pixel is found by a left→right scan, so it was "entered" moving
  // EAST from its empty west neighbor: entry direction 0. The clockwise scan
  // below then starts at backtrack+1 = NW, which correctly hugs the boundary
  // instead of stepping into the blob's interior.
  let dir = 0;
  const maxSteps = 4 * w * h; // hard safety bound

  for (let step = 0; step < maxSteps; step++) {
    // Search clockwise from just past the backtrack direction (dir+4 is the
    // pixel we came from; +5 is the next clockwise neighbor after it).
    let found = -1;
    for (let i = 0; i < 8; i++) {
      const d = (dir + 5 + i) % 8;
      const nx = cx + MOORE[d][0];
      const ny = cy + MOORE[d][1];
      if (inside(nx, ny)) {
        found = d;
        cx = nx;
        cy = ny;
        break;
      }
    }
    if (found === -1) break; // isolated pixel
    dir = found;
    // Closed the loop: back at the start after actually going somewhere.
    if (cx === sx && cy === sy && contour.length > 2) break;
    contour.push([cx, cy]);
    if (contour.length > 20000) break; // degenerate — bail out
  }
  return contour;
}

/** Perpendicular distance of p from segment a-b. */
function perpDist(p, a, b) {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-9) return Math.hypot(p[0] - a[0], p[1] - a[1]);
  const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2));
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
}

/** Ramer–Douglas–Peucker polyline simplification (iterative, stack-based). */
function simplify(points, epsilon) {
  if (points.length < 4) return points;
  const keep = new Uint8Array(points.length);
  keep[0] = 1;
  keep[points.length - 1] = 1;
  const stack = [[0, points.length - 1]];
  while (stack.length) {
    const [i0, i1] = stack.pop();
    let maxD = 0;
    let maxI = -1;
    for (let i = i0 + 1; i < i1; i++) {
      const d = perpDist(points[i], points[i0], points[i1]);
      if (d > maxD) {
        maxD = d;
        maxI = i;
      }
    }
    if (maxD > epsilon && maxI > 0) {
      keep[maxI] = 1;
      stack.push([i0, maxI], [maxI, i1]);
    }
  }
  const out = [];
  for (let i = 0; i < points.length; i++) if (keep[i]) out.push(points[i]);
  return out;
}

/** Signed polygon area (shoelace) — used to rank components by size. */
function polyArea(points) {
  let a = 0;
  for (let i = 0, n = points.length; i < n; i++) {
    const [x0, y0] = points[i];
    const [x1, y1] = points[(i + 1) % n];
    a += x0 * y1 - x1 * y0;
  }
  return Math.abs(a) / 2;
}

/**
 * Extract simplified outer contours of every connected component in a mask.
 *
 * @param {Uint8Array} mask   w*h, 1 = item pixel
 * @param {number} w
 * @param {number} h
 * @param {{ epsilon?: number, maxPolys?: number, minArea?: number }} [opts]
 * @returns {number[][][]} up to maxPolys polygons, each [[nx, ny], ...] in 0..1
 */
export function extractOutlines(mask, w, h, opts = {}) {
  const epsilon = opts.epsilon ?? 1.4;
  const maxPolys = opts.maxPolys ?? 4;
  const minArea = opts.minArea ?? 30; // px² — skip specks

  const claimed = new Uint8Array(w * h); // pixels already inside a traced component
  const polys = [];

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (mask[i] !== 1 || claimed[i]) continue;
      // Boundary start: filled pixel with an empty (or edge) west neighbor.
      if (x > 0 && mask[i - 1] === 1) continue;

      const contour = traceBoundary(mask, w, h, x, y);
      // Claim the whole component via scanline flood from the contour's bbox so
      // inner starts don't re-trace it. (Cheap approximation: claim by
      // even-odd fill of the contour's rows.)
      claimComponent(mask, claimed, w, h, x, y);

      if (contour.length < 8) continue;
      const simplified = simplify(contour, epsilon);
      if (simplified.length < 3 || polyArea(simplified) < minArea) continue;
      polys.push(simplified);
    }
  }

  polys.sort((a, b) => polyArea(b) - polyArea(a));
  return polys.slice(0, maxPolys).map((poly) => poly.map(([px, py]) => [px / w, py / h]));
}

/** Flood-claim the connected component containing (sx, sy) (scanline BFS). */
function claimComponent(mask, claimed, w, h, sx, sy) {
  const stack = [sy * w + sx];
  claimed[sy * w + sx] = 1;
  while (stack.length) {
    const i = stack.pop();
    const x = i % w;
    const y = (i / w) | 0;
    if (x > 0 && mask[i - 1] === 1 && !claimed[i - 1]) {
      claimed[i - 1] = 1;
      stack.push(i - 1);
    }
    if (x < w - 1 && mask[i + 1] === 1 && !claimed[i + 1]) {
      claimed[i + 1] = 1;
      stack.push(i + 1);
    }
    if (y > 0 && mask[i - w] === 1 && !claimed[i - w]) {
      claimed[i - w] = 1;
      stack.push(i - w);
    }
    if (y < h - 1 && mask[i + w] === 1 && !claimed[i + w]) {
      claimed[i + w] = 1;
      stack.push(i + w);
    }
  }
}

export default extractOutlines;
